import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { FieldValues, Path, UseFormReturn } from "react-hook-form";

interface FlightFormUIState {
  refs: {
    departureInputRef: RefObject<HTMLInputElement | null>;
    destinationInputRef: RefObject<HTMLInputElement | null>;
    departureDivRef: RefObject<HTMLDivElement | null>;
    destinationDivRef: RefObject<HTMLDivElement | null>;
    departureDateRef: RefObject<HTMLInputElement | null>;
  };
  ui: {
    showDepartureSelector: boolean;
    setShowDepartureSelector: (v: boolean) => void;
    showDestinationSelector: boolean;
    setShowDestinationSelector: (v: boolean) => void;
    isSearchingDeparture: boolean;
    setIsSearchingDeparture: (v: boolean) => void;
    departureDateOpen: boolean;
    setDepartureDateOpen: (v: boolean) => void;
    showPromoCode: boolean;
    setShowPromoCode: (v: boolean) => void;
    showGuestSelector: boolean;
    setShowGuestSelector: (v: boolean) => void;
    isSearchingDestination: boolean;
    setIsSearchingDestination: (v: boolean) => void;
  };
  swapCities: () => void;
}

export function useFlightFormUIState<T extends FieldValues>(
  form: UseFormReturn<T>,
): FlightFormUIState {
  const departureInputRef = useRef<HTMLInputElement>(null);
  const destinationInputRef = useRef<HTMLInputElement>(null);
  const departureDivRef = useRef<HTMLDivElement>(null);
  const destinationDivRef = useRef<HTMLDivElement>(null);
  const departureDateRef = useRef<HTMLInputElement>(null);

  const [showDepartureSelector, setShowDepartureSelector] = useState(false);
  const [showDestinationSelector, setShowDestinationSelector] = useState(false);
  const [isSearchingDeparture, setIsSearchingDeparture] = useState(false);
  const [departureDateOpen, setDepartureDateOpen] = useState(false);
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [showGuestSelector, setShowGuestSelector] = useState(false);
  const [isSearchingDestination, setIsSearchingDestination] = useState(false);

  useEffect(() => {
    function handle(event: MouseEvent | KeyboardEvent) {
      // Robust inside detection for dropdown containers and inputs
      const isInside = (el: HTMLElement | null) => {
        if (!el) return false;
        if (event instanceof MouseEvent) {
          const path = (event as MouseEvent).composedPath?.() || [];
          return path.includes(el) || el.contains(event.target as Node);
        }
        return false;
      };

      // Close on outside click (desktop only, attached conditionally below)
      if (event instanceof MouseEvent) {
        if (
          showDepartureSelector &&
          !isInside(departureDivRef.current) &&
          !isInside(departureInputRef.current)
        ) {
          setShowDepartureSelector(false);
          setIsSearchingDeparture(false);
        }
        if (
          showDestinationSelector &&
          !isInside(destinationDivRef.current) &&
          !isInside(destinationInputRef.current)
        ) {
          setShowDestinationSelector(false);
          setIsSearchingDestination(false);
        }
      }

      // Close on Escape (all devices)
      if (event instanceof KeyboardEvent && event.key === "Escape") {
        if (showDepartureSelector) {
          setShowDepartureSelector(false);
          setIsSearchingDeparture(false);
        }
        if (showDestinationSelector) {
          setShowDestinationSelector(false);
          setIsSearchingDestination(false);
        }
      }
    }

    const shouldAttach = showDepartureSelector || showDestinationSelector;

    if (shouldAttach) {
      document.addEventListener("keydown", handle);
      document.addEventListener("click", handle);
    }
    return () => {
      if (shouldAttach) {
        document.removeEventListener("keydown", handle);
        document.removeEventListener("click", handle);
      }
    };
  }, [
    showDepartureSelector,
    showDestinationSelector,
    setIsSearchingDeparture,
    setIsSearchingDestination,
  ]);
  const departureKey = "departure" as Path<T>;
  const destinationKey = "destination" as Path<T>;

  const swapCities = useCallback(() => {
    const currentDeparture = form.getValues(departureKey);
    const currentDestination = form.getValues(destinationKey);
    form.setValue(departureKey, currentDestination);
    form.setValue(destinationKey, currentDeparture);
  }, [form, departureKey, destinationKey]);

  return {
    refs: {
      departureInputRef,
      destinationInputRef,
      departureDivRef,
      destinationDivRef,
      departureDateRef,
    },
    ui: {
      showDepartureSelector,
      setShowDepartureSelector,
      showDestinationSelector,
      setShowDestinationSelector,
      isSearchingDeparture,
      setIsSearchingDeparture,
      departureDateOpen,
      setDepartureDateOpen,
      showPromoCode,
      setShowPromoCode,
      showGuestSelector,
      setShowGuestSelector,
      isSearchingDestination,
      setIsSearchingDestination,
    },
    swapCities,
  };
}
